import React, { useEffect, useState } from 'react';
import { RELEASE_NOTE_LANGUAGES } from '../../../api/firmware';
import { channelClass, firmwareChannels, formatBytes, formatDate, statusClass } from '../firmwareUtils';
import FirmwareModal from './FirmwareModal';

export function UploadFirmwareDialog({ open, form, file, error, uploading, onClose, onSubmit, onFieldChange, onFileChange }) {
  if (!open) return null;

  return (
    <FirmwareModal title="Upload Firmware" onClose={onClose}>
      <form className="firmware-form" onSubmit={onSubmit}>
        {error ? <div className="alert alert-error">{error}</div> : null}
        <label className="firmware-file-field" htmlFor="firmware-file"><span>Firmware file</span><input id="firmware-file" type="file" accept=".bin,.hex,.zip,application/octet-stream" onChange={(event) => onFileChange(event.target.files?.[0] || null)} disabled={uploading} /><small>{file ? `${file.name} (${formatBytes(file.size)})` : 'Select a firmware binary or package.'}</small></label>
        <div className="firmware-form-grid">
          <label><span>Device model</span><input value={form.deviceModel} onChange={onFieldChange('deviceModel')} placeholder="WallStation" disabled={uploading} /></label>
          <label><span>Hardware version</span><input value={form.hardwareVersion} onChange={onFieldChange('hardwareVersion')} placeholder="ESP32" disabled={uploading} /></label>
          <label><span>Firmware version</span><input value={form.firmwareVersion} onChange={onFieldChange('firmwareVersion')} placeholder="6.3.9" disabled={uploading} /></label>
          <label className="firmware-span-two"><span>Required previous version</span><input value={form.requiredFirmwareVersion} onChange={onFieldChange('requiredFirmwareVersion')} placeholder="Optional minimum firmware version" disabled={uploading} /></label>
        </div>
        <div className="firmware-modal-actions"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" type="submit" disabled={uploading}>{uploading ? 'Uploading...' : 'Upload package'}</button></div>
      </form>
    </FirmwareModal>
  );
}

const LANGUAGE_LABELS = {
  en: 'English',
  it: 'Italian',
  pl: 'Polish',
  pt: 'Portuguese',
  de: 'German',
};

function emptyReleaseNote(language = 'en') {
  return { language, title: '', body: '', isNew: true };
}

function emptyChangeLog(language = 'en') {
  return { language, title: '', body: '', isNew: true };
}

function releaseNoteLabel(language) {
  return `${LANGUAGE_LABELS[language] || language} (${String(language).toUpperCase()})`;
}

export function FirmwareDetailsDialog({
  firmware,
  detailsLoading,
  detailsError,
  onRetryDetails,
  releaseNotes,
  releaseNotesLoading,
  releaseNotesError,
  releaseNoteLoadingLanguage,
  releaseNoteSaving,
  releaseNoteDeletingLanguage,
  changeLogs,
  changeLogsLoading,
  changeLogsError,
  changeLogLoadingLanguage,
  changeLogSaving,
  changeLogDeletingLanguage,
  canManageReleaseNotes,
  canManageChangeLogs,
  onClose,
  onLoadReleaseNote,
  onSaveReleaseNote,
  onDeleteReleaseNote,
  onLoadChangeLog,
  onSaveChangeLog,
  onDeleteChangeLog,
}) {
  const [releaseNoteForm, setReleaseNoteForm] = useState(null);
  const [releaseNoteFormError, setReleaseNoteFormError] = useState('');
  const [releaseNoteToDelete, setReleaseNoteToDelete] = useState('');
  const [changeLogForm, setChangeLogForm] = useState(null);
  const [changeLogFormError, setChangeLogFormError] = useState('');
  const [changeLogToDelete, setChangeLogToDelete] = useState('');

  useEffect(() => {
    setReleaseNoteForm(null);
    setReleaseNoteFormError('');
    setReleaseNoteToDelete('');
    setChangeLogForm(null);
    setChangeLogFormError('');
    setChangeLogToDelete('');
  }, [firmware?.id]);

  if (!firmware) return null;

  const availableReleaseNoteLanguages = RELEASE_NOTE_LANGUAGES.filter((candidate) => (
    !releaseNotes.some((note) => note.language === candidate)
  ));
  const availableChangeLogLanguages = RELEASE_NOTE_LANGUAGES.filter((candidate) => (
    !changeLogs.some((changeLog) => changeLog.language === candidate)
  ));

  const addReleaseNote = () => {
    const language = availableReleaseNoteLanguages[0];
    if (!language) return;
    setReleaseNoteForm(emptyReleaseNote(language));
    setReleaseNoteFormError('');
  };

  const editReleaseNote = async (releaseNote) => {
    setReleaseNoteForm({ ...releaseNote, isNew: false });
    setReleaseNoteFormError('');
    try {
      const currentReleaseNote = await onLoadReleaseNote(releaseNote.language);
      if (currentReleaseNote) {
        setReleaseNoteForm({ ...currentReleaseNote, isNew: false });
      }
    } catch (error) {
      setReleaseNoteFormError(error instanceof Error ? error.message : String(error));
    }
  };

  const saveReleaseNote = async (event) => {
    event.preventDefault();
    if (!releaseNoteForm?.body.trim()) {
      setReleaseNoteFormError('Release note body is required.');
      return;
    }

    try {
      setReleaseNoteFormError('');
      await onSaveReleaseNote({
        language: releaseNoteForm.language,
        title: releaseNoteForm.title.trim(),
        body: releaseNoteForm.body.trim(),
      });
      setReleaseNoteForm(null);
    } catch (error) {
      setReleaseNoteFormError(error instanceof Error ? error.message : String(error));
    }
  };

  const deleteReleaseNote = async (language) => {
    try {
      setReleaseNoteFormError('');
      await onDeleteReleaseNote(language);
      setReleaseNoteToDelete('');
      if (releaseNoteForm?.language === language) {
        setReleaseNoteForm(null);
      }
    } catch (error) {
      setReleaseNoteFormError(error instanceof Error ? error.message : String(error));
    }
  };

  const addChangeLog = () => {
    const language = availableChangeLogLanguages[0];
    if (!language) return;
    setChangeLogForm(emptyChangeLog(language));
    setChangeLogFormError('');
  };

  const editChangeLog = async (changeLog) => {
    setChangeLogForm({ ...changeLog, isNew: false });
    setChangeLogFormError('');
    try {
      const currentChangeLog = await onLoadChangeLog(changeLog.language);
      if (currentChangeLog) {
        setChangeLogForm({ ...currentChangeLog, isNew: false });
      }
    } catch (error) {
      setChangeLogFormError(error instanceof Error ? error.message : String(error));
    }
  };

  const saveChangeLog = async (event) => {
    event.preventDefault();
    if (!changeLogForm?.body.trim()) {
      setChangeLogFormError('Changelog body is required.');
      return;
    }

    try {
      setChangeLogFormError('');
      await onSaveChangeLog({
        language: changeLogForm.language,
        title: changeLogForm.title.trim(),
        body: changeLogForm.body.trim(),
      });
      setChangeLogForm(null);
    } catch (error) {
      setChangeLogFormError(error instanceof Error ? error.message : String(error));
    }
  };

  const deleteChangeLog = async (language) => {
    try {
      setChangeLogFormError('');
      await onDeleteChangeLog(language);
      setChangeLogToDelete('');
      if (changeLogForm?.language === language) {
        setChangeLogForm(null);
      }
    } catch (error) {
      setChangeLogFormError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <FirmwareModal title={`Firmware ${firmware.firmwareVersion}`} onClose={onClose}>
      {detailsLoading ? <p role="status">Loading firmware details...</p> : detailsError ? <div className="alert alert-error" role="alert"><p>{detailsError}</p><button type="button" className="btn btn-secondary" onClick={onRetryDetails}>Retry details</button></div> :
      <div className="firmware-detail">
        <div className="firmware-detail-top"><div className="fw-package-channels">{firmwareChannels(firmware).length ? firmwareChannels(firmware).map((channel) => <span key={channel} className={channelClass(channel)}>{channel}</span>) : <span>Not promoted</span>}</div><span>{firmware.deviceModel} · {firmware.hardwareVersion}</span></div>
        <dl className="firmware-detail-grid"><div><dt>Package ID</dt><dd>{firmware.id}</dd></div><div><dt>Package size</dt><dd>{formatBytes(firmware.sizeInBytes)}</dd></div><div><dt>Release date</dt><dd>{firmware.releaseDate}</dd></div><div><dt>Created</dt><dd>{formatDate(firmware.createdAt)}</dd></div><div><dt>Updated</dt><dd>{formatDate(firmware.updatedAt)}</dd></div><div><dt>Required previous firmware</dt><dd>{firmware.requiredFirmwareVersion || 'None'}</dd></div><div className="firmware-span-two"><dt>Checksum</dt><dd>{firmware.checksum || 'Not available'}</dd></div><div className="firmware-span-two"><dt>SBOM keys</dt><dd>{firmware.sbomKeys?.length ? firmware.sbomKeys.map((key) => <div key={key}>{key}</div>) : 'None'}</dd></div></dl>
        <section className="firmware-release-notes" aria-labelledby="release-notes-heading">
          <div className="firmware-release-notes-heading"><h4 id="release-notes-heading">Release notes</h4>{canManageReleaseNotes && !releaseNoteForm && availableReleaseNoteLanguages.length ? <button type="button" className="btn btn-secondary" onClick={addReleaseNote}>Add release note</button> : null}</div>
          {releaseNotesError ? <div className="alert alert-error" role="alert">{releaseNotesError}</div> : null}
          {releaseNotesLoading ? <div className="firmware-release-note-empty">Loading release notes...</div> : null}
          {!releaseNotesLoading && !releaseNotesError && !releaseNotes.length ? <div className="firmware-release-note-empty">No release notes have been added.</div> : null}
          <div className="firmware-release-note-list">
            {releaseNotes.map((releaseNote) => (
              <article className="firmware-release-note" key={releaseNote.language}>
                <div className="firmware-release-note-title"><div><strong>{releaseNoteLabel(releaseNote.language)}</strong>{releaseNote.title ? <span>{releaseNote.title}</span> : null}</div>{canManageReleaseNotes ? <div className="firmware-release-note-actions"><button type="button" className="firmware-text-button" onClick={() => editReleaseNote(releaseNote)} disabled={Boolean(releaseNoteLoadingLanguage) || Boolean(releaseNoteDeletingLanguage)}>{releaseNoteLoadingLanguage === releaseNote.language ? 'Loading...' : 'Edit'}</button><button type="button" className="firmware-text-button firmware-text-danger" onClick={() => setReleaseNoteToDelete(releaseNote.language)} disabled={Boolean(releaseNoteLoadingLanguage) || Boolean(releaseNoteDeletingLanguage)}>Delete</button></div> : null}</div>
                <p>{releaseNote.body}</p>
                {releaseNoteToDelete === releaseNote.language ? <div className="firmware-release-note-confirm"><span>Delete this release note?</span><div><button type="button" className="btn btn-secondary" onClick={() => setReleaseNoteToDelete('')} disabled={releaseNoteDeletingLanguage === releaseNote.language}>Cancel</button><button type="button" className="btn btn-danger" onClick={() => deleteReleaseNote(releaseNote.language)} disabled={releaseNoteDeletingLanguage === releaseNote.language}>{releaseNoteDeletingLanguage === releaseNote.language ? 'Deleting...' : 'Delete note'}</button></div></div> : null}
              </article>
            ))}
          </div>
          {releaseNoteForm ? <form className="firmware-form firmware-release-note-form" onSubmit={saveReleaseNote}>
            <h5>{releaseNoteForm.isNew ? 'Add release note' : `Edit ${releaseNoteLabel(releaseNoteForm.language)} release note`}</h5>
            {releaseNoteFormError ? <div className="alert alert-error" role="alert">{releaseNoteFormError}</div> : null}
            <label><span>Language</span><select value={releaseNoteForm.language} disabled={!releaseNoteForm.isNew || releaseNoteSaving || Boolean(releaseNoteLoadingLanguage)} onChange={(event) => setReleaseNoteForm((current) => ({ ...current, language: event.target.value }))}>{(releaseNoteForm.isNew ? availableReleaseNoteLanguages : [releaseNoteForm.language]).map((language) => <option key={language} value={language}>{releaseNoteLabel(language)}</option>)}</select></label>
            <label><span>Release note title</span><input value={releaseNoteForm.title || ''} maxLength="180" onChange={(event) => setReleaseNoteForm((current) => ({ ...current, title: event.target.value }))} disabled={releaseNoteSaving || Boolean(releaseNoteLoadingLanguage)} /></label>
            <label><span>Release note body</span><textarea value={releaseNoteForm.body || ''} onChange={(event) => setReleaseNoteForm((current) => ({ ...current, body: event.target.value }))} disabled={releaseNoteSaving || Boolean(releaseNoteLoadingLanguage)} required rows="6" /></label>
            <div className="firmware-modal-actions"><button type="button" className="btn btn-secondary" onClick={() => { setReleaseNoteForm(null); setReleaseNoteFormError(''); }} disabled={releaseNoteSaving}>Cancel</button><button type="submit" className="btn btn-primary" disabled={releaseNoteSaving || Boolean(releaseNoteLoadingLanguage)}>{releaseNoteSaving ? 'Saving...' : 'Save release note'}</button></div>
          </form> : null}
        </section>
        <section className="firmware-release-notes" aria-labelledby="changelog-heading">
          <div className="firmware-release-notes-heading"><h4 id="changelog-heading">Changelog</h4>{canManageChangeLogs && !changeLogForm && availableChangeLogLanguages.length ? <button type="button" className="btn btn-secondary" onClick={addChangeLog}>Add changelog</button> : null}</div>
          {changeLogsError ? <div className="alert alert-error" role="alert">{changeLogsError}</div> : null}
          {changeLogsLoading ? <div className="firmware-release-note-empty">Loading changelog...</div> : null}
          {!changeLogsLoading && !changeLogsError && !changeLogs.length ? <div className="firmware-release-note-empty">No changelog entries have been added.</div> : null}
          <div className="firmware-release-note-list">
            {changeLogs.map((changeLog) => (
              <article className="firmware-release-note" key={changeLog.language}>
                <div className="firmware-release-note-title"><div><strong>{releaseNoteLabel(changeLog.language)}</strong>{changeLog.title ? <span>{changeLog.title}</span> : null}</div>{canManageChangeLogs ? <div className="firmware-release-note-actions"><button type="button" className="firmware-text-button" onClick={() => editChangeLog(changeLog)} disabled={Boolean(changeLogLoadingLanguage) || Boolean(changeLogDeletingLanguage)}>{changeLogLoadingLanguage === changeLog.language ? 'Loading...' : 'Edit'}</button><button type="button" className="firmware-text-button firmware-text-danger" onClick={() => setChangeLogToDelete(changeLog.language)} disabled={Boolean(changeLogLoadingLanguage) || Boolean(changeLogDeletingLanguage)}>Delete</button></div> : null}</div>
                <p>{changeLog.body}</p>
                {changeLogToDelete === changeLog.language ? <div className="firmware-release-note-confirm"><span>Delete this changelog entry?</span><div><button type="button" className="btn btn-secondary" onClick={() => setChangeLogToDelete('')} disabled={changeLogDeletingLanguage === changeLog.language}>Cancel</button><button type="button" className="btn btn-danger" onClick={() => deleteChangeLog(changeLog.language)} disabled={changeLogDeletingLanguage === changeLog.language}>{changeLogDeletingLanguage === changeLog.language ? 'Deleting...' : 'Delete changelog'}</button></div></div> : null}
              </article>
            ))}
          </div>
          {changeLogForm ? <form className="firmware-form firmware-release-note-form" onSubmit={saveChangeLog}>
            <h5>{changeLogForm.isNew ? 'Add changelog' : `Edit ${releaseNoteLabel(changeLogForm.language)} changelog`}</h5>
            {changeLogFormError ? <div className="alert alert-error" role="alert">{changeLogFormError}</div> : null}
            <label><span>Language</span><select value={changeLogForm.language} disabled={!changeLogForm.isNew || changeLogSaving || Boolean(changeLogLoadingLanguage)} onChange={(event) => setChangeLogForm((current) => ({ ...current, language: event.target.value }))}>{(changeLogForm.isNew ? availableChangeLogLanguages : [changeLogForm.language]).map((language) => <option key={language} value={language}>{releaseNoteLabel(language)}</option>)}</select></label>
            <label><span>Changelog title</span><input value={changeLogForm.title || ''} maxLength="180" onChange={(event) => setChangeLogForm((current) => ({ ...current, title: event.target.value }))} disabled={changeLogSaving || Boolean(changeLogLoadingLanguage)} /></label>
            <label><span>Changelog body</span><textarea value={changeLogForm.body || ''} onChange={(event) => setChangeLogForm((current) => ({ ...current, body: event.target.value }))} disabled={changeLogSaving || Boolean(changeLogLoadingLanguage)} required rows="6" /></label>
            <div className="firmware-modal-actions"><button type="button" className="btn btn-secondary" onClick={() => { setChangeLogForm(null); setChangeLogFormError(''); }} disabled={changeLogSaving}>Cancel</button><button type="submit" className="btn btn-primary" disabled={changeLogSaving || Boolean(changeLogLoadingLanguage)}>{changeLogSaving ? 'Saving...' : 'Save changelog'}</button></div>
          </form> : null}
        </section>
        <section className="firmware-release-notes" aria-labelledby="package-promotions-heading">
          <h4 id="package-promotions-heading">Promotion history</h4>
          {firmware.promotions?.length ? <ul className="fw-promotion-history">{[...firmware.promotions].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))).map((promotion) => <li key={promotion.id}><span className={channelClass(promotion.toChannel)}>{promotion.toChannel}</span><span>{formatDate(promotion.createdAt)}</span>{promotion.promotedBy ? <span>{promotion.promotedBy}</span> : null}</li>)}</ul> : <div className="firmware-release-note-empty">No promotions yet.</div>}
        </section>
      </div>}
    </FirmwareModal>
  );
}


export function InstallFirmwareDialog({ open, firmware, device, channel, reason, requesting, onClose, onReasonChange, onSubmit }) {
  if (!open) return null;

  return (
    <FirmwareModal title="Install Firmware" onClose={onClose}>
      <div className="firmware-confirmation">
        <p>Request <strong>{firmware?.version}</strong>{firmware?.releaseDate ? ` (${firmware.releaseDate})` : ''} for <strong>{device.name}</strong>{channel ? <> via <strong>{channel}</strong></> : null}.</p>
        <label><span>Reason or ticket reference <span className="firmware-required" aria-hidden="true">*</span></span><input value={reason} onChange={(event) => onReasonChange(event.target.value)} placeholder="Customer escalation CC2OC-1234" required /></label>
        <div className="firmware-modal-actions"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={requesting} onClick={onSubmit}>{requesting ? 'Requesting...' : 'Request update'}</button></div>
      </div>
    </FirmwareModal>
  );
}

export function RemoveFirmwareDialog({ firmware, removing, error, onClose, onConfirm }) {
  if (!firmware) return null;

  return (
    <FirmwareModal title="Remove Firmware Package" onClose={removing ? () => {} : onClose}>
      <div className="firmware-confirmation">
        <p>Remove <strong>{firmware.deviceModel} {firmware.firmwareVersion}</strong> from the firmware catalogue? This permanently removes the package metadata and associated firmware files.</p>
        {error ? <div className="alert alert-error">{error}</div> : null}
        <div className="firmware-modal-actions"><button type="button" className="btn btn-secondary" onClick={onClose} disabled={removing}>Cancel</button><button type="button" className="btn btn-danger" onClick={onConfirm} disabled={removing}>{removing ? 'Removing...' : 'Remove firmware'}</button></div>
      </div>
    </FirmwareModal>
  );
}

export function NewBatchRolloutDialog({ open, form, packages, batchCount, onClose, onSubmit, onChange }) {
  if (!open) return null;
  const deviceModels = [...new Set(packages.map((item) => item.deviceModel))];
  const releasablePackages = packages.filter((item) => !item.promotionsError && firmwareChannels(item).some((channel) => channel !== 'INTERNAL'));

  return (
    <FirmwareModal title="New Batch Rollout" onClose={onClose} wide>
      <form className="firmware-form" onSubmit={onSubmit}>
        <div className="firmware-form-grid">
          <label><span>Region</span><select value={form.region} onChange={(event) => onChange('region', event.target.value)}><option>EU Central 1</option><option>US East 1</option></select></label>
          <label><span>Device model</span><select value={form.deviceModel} onChange={(event) => onChange('deviceModel', event.target.value)}>{deviceModels.map((model) => <option key={model}>{model}</option>)}</select></label>
          <label className="firmware-span-two"><span>Firmware package</span><select value={form.firmwareId} onChange={(event) => onChange('firmwareId', event.target.value)}>{releasablePackages.map((firmware) => <option key={firmware.id} value={firmware.id}>{firmware.deviceModel} · {firmware.firmwareVersion} · {firmwareChannels(firmware).join(', ')}</option>)}</select></label>
          <label><span>Target</span><select value={form.target} onChange={(event) => onChange('target', event.target.value)}><option>All eligible devices</option><option>Pilot customer group</option><option>Selected sites</option></select></label>
          <label><span>Reason or ticket reference</span><input value={form.ticket} onChange={(event) => onChange('ticket', event.target.value)} placeholder="CC2OC-1234" required /></label>
        </div>
        <label className="firmware-check"><input type="checkbox" checked={form.onlineOnly} onChange={(event) => onChange('onlineOnly', event.target.checked)} /> <span>Only include currently online devices</span></label>
        <div className="firmware-wave-grid"><label><span>Wave 1</span><input type="number" min="0" value={form.firstWave} onChange={(event) => onChange('firstWave', event.target.value)} /></label><label><span>Wave 2</span><input type="number" min="0" value={form.secondWave} onChange={(event) => onChange('secondWave', event.target.value)} /></label><label><span>Wave 3</span><input type="number" min="0" value={form.thirdWave} onChange={(event) => onChange('thirdWave', event.target.value)} /></label><div><span>Devices in rollout</span><strong>{batchCount}</strong></div></div>
        <div className="firmware-modal-actions"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={!batchCount}>Create batch</button></div>
      </form>
    </FirmwareModal>
  );
}

export function BatchDetailsDialog({ batch, onClose }) {
  if (!batch) return null;

  return (
    <FirmwareModal title={batch.id} onClose={onClose}>
      <div className="firmware-detail">
        <div className="firmware-detail-top"><span className={statusClass(batch.status)}>{batch.status}</span><span>{batch.region}</span></div>
        <dl className="firmware-detail-grid"><div><dt>Firmware</dt><dd>{batch.firmwareVersion}</dd></div><div><dt>Device model</dt><dd>{batch.deviceModel}</dd></div><div><dt>Target devices</dt><dd>{batch.total}</dd></div><div><dt>Last updated</dt><dd>{batch.updated}</dd></div></dl>
        <div className="firmware-rollout-breakdown"><div><span>Queued</span><strong>{batch.queued}</strong></div><div><span>In progress</span><strong>{batch.active}</strong></div><div><span>Completed</span><strong>{batch.complete}</strong></div><div><span>Failed</span><strong>{batch.failed}</strong></div></div>
      </div>
    </FirmwareModal>
  );
}
